import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

/**
 * Right-hand slide-over used by the admin create/edit form and detail view.
 * Presentational: mount it when visible; the parent controls its lifetime.
 */
export default function Drawer({
  onClose,
  eyebrow,
  title,
  headerRight,
  footer,
  width = "max-w-[520px]",
  children,
}: {
  onClose: () => void;
  eyebrow?: ReactNode;
  title: ReactNode;
  headerRight?: ReactNode;
  footer?: ReactNode;
  width?: string;
  children: ReactNode;
}) {
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.aside
        className={`glass fixed right-0 top-0 z-[56] flex h-screen w-full ${width} flex-col overflow-hidden !rounded-none border-l border-white/10`}
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div className="min-w-0">
            {eyebrow && <div className="section-tag">{eyebrow}</div>}
            <h2 className="truncate text-lg font-semibold">{title}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerRight}
            <button onClick={onClose} className="ml-1 text-slatey hover:text-ink" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="border-t border-white/10 bg-ocean-900/70 p-4 backdrop-blur">{footer}</div>
        )}
      </motion.aside>
    </AnimatePresence>
  );
}
